<p class="font-bold line-height-2" i18n>
  The Proxy-Call Session Control Function (P-CSCF) is the first contact point for the users of IMS network. The P-CSCF functions as a proxy
  server for the device and all Session Initiation Protocol (SIP) signaling traffic must go through the P-CSCF.
</p>

<p class="font-bold line-height-2" i18n>Let's define the P-CSCF configuration.</p>

<div class="mx-6">
  <rs-error-message *ngIf="errorFound" class="mb-3" [message]="errorMessage" />

  <div style="width: 1200px">
    <p-table
      showGridlines="true"
      stripedRows="true"
      #pcscfAddressTable
      [value]="profileValues.isimConfiguration.pcscfConfiguration"
      [(selection)]="selectedAddresses"
      [resizableColumns]="true"
      dataKey="id"
      styleClass="p-datatable-striped p-datatable-gridlines"
    >
      <ng-template pTemplate="caption">
        <div class="flex align-items-center gap-2">
          <p-button i18n-label label="Add" icon="pi pi-plus-circle" size="small" [outlined]="true" (onClick)="addAddress()"></p-button>
          <p-button
            i18n-label
            label="Delete"
            icon="pi pi-trash"
            size="small"
            severity="danger"
            [outlined]="true"
            (onClick)="deleteAddress()"
            [disabled]="!selectedAddresses || !selectedAddresses.length"
          ></p-button>
        </div>
      </ng-template>
      <ng-template pTemplate="colgroup">
        <colgroup>
          <col style="width: 3rem" />
          <col style="width: 7rem" />
          <col style="width: 14rem" />
          <col />
        </colgroup>
      </ng-template>
      <ng-template pTemplate="header">
        <tr>
          <th>
            <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
          </th>
          <th pResizableColumn i18n>Record #</th>
          <th pResizableColumn i18n>Address Type</th>
          <th pResizableColumn i18n>P-CSCF Address</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-address let-rowIndex="rowIndex">
        <tr>
          <td style="text-align: center">
            <p-tableCheckbox [value]="address"></p-tableCheckbox>
          </td>
          <td style="text-align: center">{{ rowIndex + 1 }}</td>
          <td>
            <p-select
              styleClass="maximize-inputs"
              [options]="addressTypes"
              [(ngModel)]="address.addressType"
              dataKey="value"
              optionLabel="label"
              i18n-placeholder
              placeholder="Please Select:"
              appendTo="body"
              (onChange)="handleFormEvents()"
            ></p-select>
          </td>
          <td *ngIf="address.addressType === null"></td>
          <td class="text-align: center" *ngIf="address.addressType && +address.addressType.value === 1">
            <div style="width: 600px; margin: auto">
              <input
                type="text"
                pInputText
                class="maximize-inputs"
                [(ngModel)]="address.pcscfAddressFQDN"
                [maxlength]="fqdnMaxLength"
                (input)="handleFormEvents()"
              />
            </div>
          </td>
          <td class="text-align: center" *ngIf="address.addressType && +address.addressType.value === 2">
            <div style="width: 600px; margin: auto">
              <table cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <p-inputNumber
                      class="maximize-inputs"
                      inputStyleClass="maximize-inputs console-text"
                      [(ngModel)]="address.pcscfAddressIPv4.address1"
                      [min]="0"
                      [max]="255"
                      [maxlength]="3"
                      [useGrouping]="false"
                      (onBlur)="handleFormEvents()"
                    ></p-inputNumber>
                  </td>
                  <td style="width: 1rem; text-align: center"><span>.</span></td>
                  <td>
                    <p-inputNumber
                      class="maximize-inputs"
                      inputStyleClass="maximize-inputs console-text"
                      [(ngModel)]="address.pcscfAddressIPv4.address2"
                      [min]="0"
                      [max]="255"
                      [maxlength]="3"
                      [useGrouping]="false"
                      (onBlur)="handleFormEvents()"
                    ></p-inputNumber>
                  </td>
                  <td style="width: 1rem; text-align: center"><span>.</span></td>
                  <td>
                    <p-inputNumber
                      class="maximize-inputs"
                      inputStyleClass="maximize-inputs console-text"
                      [(ngModel)]="address.pcscfAddressIPv4.address3"
                      [min]="0"
                      [max]="255"
                      [maxlength]="3"
                      [useGrouping]="false"
                      (onBlur)="handleFormEvents()"
                    ></p-inputNumber>
                  </td>
                  <td style="width: 1rem; text-align: center"><span>.</span></td>
                  <td>
                    <p-inputNumber
                      class="maximize-inputs"
                      inputStyleClass="maximize-inputs console-text"
                      [(ngModel)]="address.pcscfAddressIPv4.address4"
                      [min]="0"
                      [max]="255"
                      [maxlength]="3"
                      [useGrouping]="false"
                      (onBlur)="handleFormEvents()"
                    ></p-inputNumber>
                  </td>
                </tr>
              </table>
            </div>
          </td>
          <td class="text-align: center" *ngIf="address.addressType && +address.addressType.value === 3">
            <div style="margin: auto">
              <table cellspacing="0" cellpadding="0">
                <tr>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address1"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address2"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address3"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address4"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address5"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address6"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address7"
                      (input)="handleFormEvents()"
                    />
                  </td>
                  <td style="width: 1rem; text-align: center"><span>:</span></td>
                  <td style="min-width: 3.5rem">
                    <input
                      pInputText
                      rsHexOnlyInput
                      class="maximize-inputs console-text"
                      [maxlength]="4"
                      [(ngModel)]="address.pcscfAddressIPv6.address8"
                      (input)="handleFormEvents()"
                    />
                  </td>
                </tr>
              </table>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" i18n>No P-CSCF addresses defined.</td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>
